import { useEffect, useState, useCallback } from 'react';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useUser } from './useUser';

interface UsePasswordOnboardingResult {
  /** null = aún no se sabe; true = el user ya tiene password permanente; false = falta crearla. */
  passwordSet: boolean | null;
  /** El bootstrap creó el row en public.usuarios este ciclo (logging/UI). */
  bootstrapped: boolean;
  loading: boolean;
  error: Error | null;
  /** Reload manual tras updateUser({password}) + mark_password_set. */
  refresh: () => Promise<void>;
}

/**
 * Estado del onboarding del usuario actual:
 *   - bootstrap automático: si el user no tiene row en public.usuarios
 *     (caso típico: primer login vía magic link), llama el RPC
 *     `bootstrap_usuario_from_allowed_email` que crea el row con scope/rol
 *     desde `allowed_emails`.
 *   - lectura del flag `password_set` para decidir si mostrar
 *     SetPasswordModal post-MFA.
 *
 * Idempotente: el RPC bootstrap retorna el row existente sin tocar nada
 * cuando ya existe.
 */
export function usePasswordOnboarding(): UsePasswordOnboardingResult {
  const { user } = useUser();
  const [passwordSet, setPasswordSet] = useState<boolean | null>(null);
  const [bootstrapped, setBootstrapped] = useState(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async () => {
    if (!user?.id || !isSupabaseConfigured()) {
      setPasswordSet(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const sb = getSupabase();
      // 1. Intentar leer la fila propia. RLS permite SELECT del propio uuid.
      const { data, error: qErr } = await sb
        .from('usuarios')
        .select('id, password_set')
        .eq('id', user.id)
        .maybeSingle();
      if (qErr) throw qErr;
      if (data) {
        setPasswordSet(Boolean(data.password_set));
        return;
      }
      // 2. No existe — disparar bootstrap (un solo call, idempotente).
      const { data: bootstrapData, error: bErr } = await sb.rpc('bootstrap_usuario_from_allowed_email');
      if (bErr) throw bErr;
      // CRÍTICO: tras crear el row en public.usuarios, refresh el JWT para
      // que el custom_access_token_hook se re-ejecute y agregue rol_app +
      // tenant_slug a app_metadata. Sin esto, el AuthGate seguiría viendo
      // un JWT sin claims → fallaría el check de `allowed` con "Sin permisos"
      // aunque el user sí esté autorizado.
      const { error: refreshErr } = await sb.auth.refreshSession();
      if (refreshErr) {
        // No bloquear — el user sigue logueado, solo perderá rol_app hasta el
        // próximo refresh natural (~1h). Logueamos para Sentry.
        console.warn('[usePasswordOnboarding] refreshSession fallo:', refreshErr.message);
      }
      const row = (bootstrapData as { password_set?: boolean } | null) ?? null;
      setPasswordSet(row ? Boolean(row.password_set) : false);
      setBootstrapped(true);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
      setPasswordSet(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { void load(); }, [load]);

  return { passwordSet, bootstrapped, loading, error, refresh: load };
}
